/**
 * Octopus IoT Platform - Geofences Page
 * FASE 2: CRUD page for managing geofences
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  MapPin, 
  Plus, 
  Pencil, 
  Trash2, 
  FileUp,
  Download,
  Loader2,
  RefreshCw,
  Search,
  MapIcon,
  Layers,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  MoveRight,
  LocateFixed,
  Pentagon,
  Ungroup,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { geofenceApi } from '@/services/geofenceApi';
import { routesApi } from '@/services/routesApi';
import GeofenceMap from '@/components/GeofenceMap';
import GeofenceUploadModal from '@/components/GeofenceUploadModal';
import MapLayerControl from '@/components/map/MapLayerControl';
import { MapContainer, Marker, Polygon, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTimezone } from '@/contexts/TimezoneContext';

// Color presets for editing
const COLOR_PRESETS = [
  { value: '#FF6B35', label: 'Laranja' },
  { value: '#22C55E', label: 'Verde' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#F59E0B', label: 'Amarelo' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#8B5CF6', label: 'Roxo' },
];

// Operational classification (role) of a geofence. Mirrors the backend
// GeofenceType enum; persisted server-side on the geofence document.
const GEOFENCE_TYPE_OPTIONS = [
  { value: 'none', label: 'Nenhum' },
  { value: 'safe', label: 'Segura' },
  { value: 'danger', label: 'Restrita / Perigosa' },
  { value: 'loading', label: 'Carregamento' },
  { value: 'port', label: 'Porto de descarregamento' },
  { value: 'anm', label: 'Área ANM' },
  // B4 (2026-07-04): camada de ANOTAÇÃO do classificador — nunca cria etapa;
  // o Parado dentro dela ganha "Ponto de apoio: X" no motivo de parada.
  { value: 'apoio', label: 'Ponto de apoio' },
  // 2026-07-04 (auditoria das geofences sem tipo): balizamento/faroletes/entradas
  // de canal — documental, no molde do ANM; fora do classificador e dos alertas.
  { value: 'sinalizacao', label: 'Sinais de Navegação' },
];

const getGeofenceTypeLabel = (value) =>
  GEOFENCE_TYPE_OPTIONS.find((option) => option.value === value)?.label || 'Nenhum';

const areCoordsEqual = (a, b) => (
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length >= 2 &&
  b.length >= 2 &&
  a[0] === b[0] &&
  a[1] === b[1]
);

const getEditableVerticesFromPolygon = (polygon) => {
  const ring = polygon?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 3) return [];
  if (ring.length > 1 && areCoordsEqual(ring[0], ring[ring.length - 1])) {
    return ring.slice(0, -1);
  }
  return ring.slice();
};

const toClosedGeoJsonPolygon = (vertices) => ({
  type: 'Polygon',
  coordinates: [
    [...vertices, vertices[0]]
  ]
});

const getGeometryType = (geofence) => geofence?.geometry_type || (geofence?.point ? 'waypoint' : 'polygon');

const ensureString = (value, fallback = '') => (
  typeof value === 'string' ? value : fallback
);

const getGeofenceDisplayName = (geofence) => (
  ensureString(geofence?.name, '').trim() || 'Geofence sem nome'
);

const getSafeDescription = (geofence) => ensureString(geofence?.description, '');

const normalizeHexColor = (value, fallback = '#FF6B35') => (
  /^#[0-9A-Fa-f]{6}$/.test(value || '') ? value.toUpperCase() : fallback
);

const getFillMode = (item) => {
  if (item?.fill_enabled === false) return 'outline';
  const opacity = Number(item?.fill_opacity);
  if (Number.isFinite(opacity) && opacity >= 0.99) return 'solid';
  return 'transparent';
};

const getTransparencyPercent = (item, fallbackOpacity = 0.3) => {
  const opacity = Number.isFinite(Number(item?.fill_opacity))
    ? Number(item.fill_opacity)
    : fallbackOpacity;
  return Math.round((1 - Math.max(0, Math.min(1, opacity))) * 100);
};

const getFillOpacityFromStyle = (fillMode, transparencyPercent) => {
  if (fillMode === 'outline') return 0;
  if (fillMode === 'solid') return 1;
  return Math.max(0, Math.min(100, 100 - Number(transparencyPercent || 0))) / 100;
};

const getSafeDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const createVertexIcon = (isSelected = false) => {
  const size = isSelected ? 16 : 12;
  const borderColor = isSelected ? '#facc15' : '#ffffff';
  const fillColor = isSelected ? '#0284c7' : '#0ea5e9';
  const shadow = isSelected ? '0 0 0 2px rgba(250, 204, 21, 0.35)' : '0 0 0 1px rgba(2, 6, 23, 0.7)';

  return L.divIcon({
    className: 'geofence-edit-vertex',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 9999px;
        border: 2px solid ${borderColor};
        background: ${fillColor};
        box-shadow: ${shadow};
      "></div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

const FitEditBounds = ({ vertices, fitToken }) => {
  const map = useMap();
  const verticesRef = useRef(vertices);

  useEffect(() => {
    verticesRef.current = vertices;
  }, [vertices]);

  useEffect(() => {
    const currentVertices = verticesRef.current;
    if (!Array.isArray(currentVertices) || currentVertices.length < 3) return;
    const bounds = currentVertices.map(([lon, lat]) => [lat, lon]);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [fitToken, map]);

  return null;
};

const StyleControls = ({
  color,
  setColor,
  borderColor,
  setBorderColor,
  borderWidth,
  setBorderWidth,
  fillMode,
  setFillMode,
  transparencyPercent,
  setTransparencyPercent,
}) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-sm font-medium">Cor do preenchimento</Label>
      <div className="flex flex-wrap gap-2">
        {COLOR_PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            className={cn(
              "w-8 h-8 rounded-full border-2 transition-all",
              color === preset.value ? "border-white scale-110" : "border-transparent"
            )}
            style={{ backgroundColor: preset.value }}
            onClick={() => setColor(preset.value)}
            title={preset.label}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="color"
          value={normalizeHexColor(color)}
          onChange={(e) => setColor(e.target.value.toUpperCase())}
          className="h-9 w-14 p-1"
          aria-label="Selecionar cor personalizada"
        />
        <Input
          value={color}
          onChange={(e) => setColor(e.target.value.toUpperCase())}
          onBlur={() => setColor((current) => normalizeHexColor(current))}
          className="h-9 w-28 font-mono"
          aria-label="Cor hexadecimal"
        />
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Cor da borda</Label>
        <div className="flex items-center gap-2">
          <Input
            type="color"
            value={normalizeHexColor(borderColor)}
            onChange={(e) => setBorderColor(e.target.value.toUpperCase())}
            className="h-9 w-14 p-1"
            aria-label="Selecionar cor da borda"
          />
          <Input
            value={borderColor}
            onChange={(e) => setBorderColor(e.target.value.toUpperCase())}
            onBlur={() => setBorderColor((current) => normalizeHexColor(current))}
            className="h-9 w-28 font-mono"
            aria-label="Cor hexadecimal da borda"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Espessura da borda</Label>
        <Input
          type="number"
          min="1"
          max="12"
          value={borderWidth}
          onChange={(e) => setBorderWidth(Number(e.target.value))}
          className="h-9"
        />
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Preenchimento</Label>
        <Select value={fillMode} onValueChange={setFillMode}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="solid">Sólido</SelectItem>
            <SelectItem value="transparent">Transparente</SelectItem>
            <SelectItem value="outline">Só borda</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {fillMode === 'transparent' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Transparência (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={transparencyPercent}
            onChange={(e) => setTransparencyPercent(Number(e.target.value))}
            className="h-9"
          />
        </div>
      )}
    </div>
  </div>
);

const GeofencesPage = () => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const [loading, setLoading] = useState(true);
  const [geofences, setGeofences] = useState([]);
  const [managedGroups, setManagedGroups] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadTargetGroup, setUploadTargetGroup] = useState(null);
  const [editingGeofence, setEditingGeofence] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [moveDialogGeofence, setMoveDialogGeofence] = useState(null);
  const [moveTargetGroupId, setMoveTargetGroupId] = useState('__none__');
  const [coordinateDialogGroup, setCoordinateDialogGroup] = useState(null);
  const [coordinateName, setCoordinateName] = useState('');
  const [coordinateLat, setCoordinateLat] = useState('');
  const [coordinateLon, setCoordinateLon] = useState('');
  const [polygonDialogGroup, setPolygonDialogGroup] = useState(null);
  const [polygonName, setPolygonName] = useState('');
  const [polygonCoordinatesText, setPolygonCoordinatesText] = useState('');
  const [styleDialogGroup, setStyleDialogGroup] = useState(null);
  const [groupStyleColor, setGroupStyleColor] = useState('#FF6B35');
  const [groupStyleBorderColor, setGroupStyleBorderColor] = useState('#FF6B35');
  const [groupStyleBorderWidth, setGroupStyleBorderWidth] = useState(2);
  const [groupStyleFillMode, setGroupStyleFillMode] = useState('transparent');
  const [groupStyleTransparencyPercent, setGroupStyleTransparencyPercent] = useState(70);
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editColor, setEditColor] = useState('#FF6B35');
  const [editBorderColor, setEditBorderColor] = useState('#FF6B35');
  const [editBorderWidth, setEditBorderWidth] = useState(2);
  const [editFillMode, setEditFillMode] = useState('transparent');
  const [editTransparencyPercent, setEditTransparencyPercent] = useState(70);
  const [editType, setEditType] = useState('none');
  const [editVisible, setEditVisible] = useState(true);
  const [editRouteId, setEditRouteId] = useState('');
  const [editBaseCarga, setEditBaseCarga] = useState(false);  // âncora de viagem (loading) — SPEC §5
  const [editGeometryType, setEditGeometryType] = useState('polygon');
  const [editVertices, setEditVertices] = useState([]);
  const [originalEditVertices, setOriginalEditVertices] = useState([]);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);
  const [editFitToken, setEditFitToken] = useState(0);
  const editFileInputRef = useRef(null);
  const [replaceParsing, setReplaceParsing] = useState(false);
  const [replaceCandidates, setReplaceCandidates] = useState([]);
  const [replaceFileName, setReplaceFileName] = useState('');

  // ⌘K deep-link (D-07): /geofences?action=new opens the same create modal as the
  // "Nova Geofence" button. Additive only — no other geofence behavior changes.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setUploadTargetGroup(null);
      setUploadModalOpen(true);
    }
  }, [searchParams]);

  // Load geofences
  useEffect(() => {
    loadGeofences();
  }, []);
  
  const loadGeofences = async () => {
    setLoading(true);
    try {
      const [response, groupsResponse, routesResponse] = await Promise.all([
        geofenceApi.listGeofences(false),
        geofenceApi.listGeofenceGroups().catch(() => ({ data: { groups: [] } })),
        routesApi.listRoutes().catch(() => ({ data: { routes: [] } })),
      ]);
      const normalizedGeofences = (response.data.geofences || []).map((gf) => ({
        ...gf,
        name: getGeofenceDisplayName(gf),
        description: getSafeDescription(gf),
      }));
      setGeofences(normalizedGeofences);
      setManagedGroups(groupsResponse.data.groups || []);
      setRoutes(routesResponse.data.routes || []);
    } catch (error) {
      console.error('Error loading geofences:', error);
      toast.error('Erro ao carregar geofences');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter geofences by search
  const filteredGeofences = useMemo(() => {
    const normalizedSearch = searchTerm.toLowerCase();
    return geofences.filter((gf) =>
      getGeofenceDisplayName(gf).toLowerCase().includes(normalizedSearch) ||
      getSafeDescription(gf).toLowerCase().includes(normalizedSearch)
    );
  }, [geofences, searchTerm]);

  const groupedGeofences = useMemo(() => {
    const groups = new Map();

    managedGroups.forEach((group) => {
      if (!group?.id) return;
      if (searchTerm && !ensureString(group.name, '').toLowerCase().includes(searchTerm.toLowerCase())) {
        return;
      }
      groups.set(group.id, {
        key: group.id,
        sourceGroupId: group.id,
        name: ensureString(group.name, '').trim() || 'Grupo sem nome',
        description: getSafeDescription(group),
        sourceFilename: null,
        items: [],
      });
    });

    filteredGeofences.forEach((gf) => {
      const groupKey = gf.source_group_id || `single:${gf.id}`;
      const current = groups.get(groupKey) || {
        key: groupKey,
        sourceGroupId: gf.source_group_id || null,
        name: ensureString(gf.source_group_name, '').trim() || ensureString(gf.source_filename, '').trim() || getGeofenceDisplayName(gf),
        sourceFilename: gf.source_filename || null,
        items: [],
      };
      current.items.push(gf);
      groups.set(groupKey, current);
    });

    return Array.from(groups.values())
      .map((group) => {
        const enabledCount = group.items.filter((item) => item.enabled).length;
        return {
          ...group,
          count: group.items.length,
          enabledCount,
          allEnabled: enabledCount === group.items.length,
          partialEnabled: enabledCount > 0 && enabledCount < group.items.length,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [filteredGeofences, managedGroups, searchTerm]);

  useEffect(() => {
    if (!groupedGeofences.length) return;
    setExpandedGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      groupedGeofences.forEach((group) => {
        if (next[group.key] === undefined) {
          next[group.key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupedGeofences]);
  
  // Handle toggle enabled
  const handleToggleEnabled = async (geofence) => {
    try {
      await geofenceApi.updateGeofence(geofence.id, { enabled: !geofence.enabled });
      setGeofences(prev => prev.map(gf => 
        gf.id === geofence.id ? { ...gf, enabled: !gf.enabled } : gf
      ));
      toast.success(geofence.enabled ? 'Geofence desativada' : 'Geofence ativada');
    } catch (error) {
      console.error('Error toggling geofence:', error);
      toast.error('Erro ao atualizar geofence');
    }
  };

  const handleToggleGroupEnabled = async (group) => {
    const targetEnabled = !group.allEnabled;

    try {
      if (group.sourceGroupId) {
        await geofenceApi.toggleGeofenceGroup(group.sourceGroupId, targetEnabled);
        setGeofences((prev) =>
          prev.map((gf) =>
            gf.source_group_id === group.sourceGroupId
              ? { ...gf, enabled: targetEnabled }
              : gf
          )
        );
        toast.success(
          targetEnabled
            ? `Grupo "${group.name}" ativado`
            : `Grupo "${group.name}" desativado`
        );
        return;
      }

      if (group.items.length === 1) {
        await handleToggleEnabled(group.items[0]);
        return;
      }

      await Promise.all(
        group.items.map((item) =>
          geofenceApi.updateGeofence(item.id, { enabled: targetEnabled })
        )
      );
      setGeofences((prev) =>
        prev.map((gf) =>
          group.items.some((item) => item.id === gf.id)
            ? { ...gf, enabled: targetEnabled }
            : gf
        )
      );
      toast.success(targetEnabled ? 'Grupo ativado' : 'Grupo desativado');
    } catch (error) {
      console.error('Error toggling geofence group:', error);
      toast.error('Erro ao atualizar grupo de geofences');
    }
  };
  
  // Open edit modal
  const handleEdit = (geofence) => {
    const geometryType = getGeometryType(geofence);
    const initialVertices = geometryType === 'polygon'
      ? getEditableVerticesFromPolygon(geofence.polygon).map(([lon, lat]) => [lon, lat])
      : [];
    setEditingGeofence(geofence);
    setEditGeometryType(geometryType);
    setEditName(geofence.name);
    setEditDescription(geofence.description || '');
    setEditColor(geofence.color || '#FF6B35');
    setEditBorderColor(geofence.border_color || geofence.color || '#FF6B35');
    setEditBorderWidth(geofence.border_width || 2);
    setEditFillMode(getFillMode(geofence));
    setEditTransparencyPercent(getTransparencyPercent(geofence, 0.3));
    setEditType(geofence.geofence_type || 'none');
    setEditVisible(geofence.visible !== false);
    setEditRouteId(geofence.route_id || '');
    setEditBaseCarga(geofence.base_carga === true);
    setEditVertices(initialVertices);
    setOriginalEditVertices(initialVertices.map(([lon, lat]) => [lon, lat]));
    setSelectedVertexIndex(null);
    setEditFitToken((prev) => prev + 1);
  };

  const handleVertexDrag = (index, lat, lng) => {
    setSelectedVertexIndex(index);
    setEditVertices(prev => prev.map((vertex, i) => (
      i === index ? [lng, lat] : vertex
    )));
  };

  const handleInsertVertex = (edgeIndex) => {
    setEditVertices(prev => {
      if (prev.length < 2) return prev;
      const nextIndex = (edgeIndex + 1) % prev.length;
      const [lon1, lat1] = prev[edgeIndex];
      const [lon2, lat2] = prev[nextIndex];
      const midpoint = [(lon1 + lon2) / 2, (lat1 + lat2) / 2];
      const updated = [...prev];
      updated.splice(nextIndex, 0, midpoint);
      setSelectedVertexIndex(nextIndex);
      return updated;
    });
  };

  const handleRemoveVertex = (index) => {
    setEditVertices(prev => {
      if (prev.length <= 3) {
        toast.error('A geofence precisa ter pelo menos 3 vértices');
        return prev;
      }
      setSelectedVertexIndex(current => {
        if (current === null) return null;
        if (current === index) return null;
        if (current > index) return current - 1;
        return current;
      });
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleResetPolygon = () => {
    if (!originalEditVertices.length) return;
    setEditVertices(originalEditVertices.map(([lon, lat]) => [lon, lat]));
    setSelectedVertexIndex(null);
    toast.success('Área restaurada para o polígono original');
  };

  // Replace the polygon vertices from a KMZ/KML file, keeping the same geofence
  // (and therefore all linked alert rules). Only the in-memory vertices change;
  // the user reviews on the map and clicks "Salvar" to persist.
  const applyImportedPolygon = (geometry) => {
    const vertices = getEditableVerticesFromPolygon(geometry?.polygon).map(([lon, lat]) => [lon, lat]);
    if (vertices.length < 3) {
      toast.error('Polígono inválido no arquivo selecionado (mínimo de 3 vértices)');
      return;
    }
    setEditVertices(vertices);
    setSelectedVertexIndex(null);
    setEditFitToken((prev) => prev + 1);
    toast.success('Vértices substituídos pelo arquivo. Revise no mapa e clique em Salvar.');
  };

  const handleSelectReplaceFile = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;

    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!['.kmz', '.kml'].includes(ext)) {
      toast.error('Arquivo inválido', { description: 'Selecione um arquivo .kmz ou .kml' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande', { description: 'O arquivo deve ter no máximo 10MB' });
      return;
    }

    setReplaceParsing(true);
    try {
      const response = await geofenceApi.parseFile(file);
      const geometries = response.data?.geometries || [];
      const polygons = geometries.filter((g) => g?.geometry_type === 'polygon' && g?.polygon);
      if (!polygons.length) {
        toast.error('Nenhum polígono encontrado', {
          description: 'O arquivo selecionado não contém polígonos para substituir esta geofence.',
        });
        return;
      }
      if (polygons.length === 1) {
        applyImportedPolygon(polygons[0]);
      } else {
        setReplaceFileName(file.name);
        setReplaceCandidates(polygons);
      }
    } catch (error) {
      console.error('Error parsing geofence file:', error);
      const detail = error.response?.data?.detail || 'Erro ao processar arquivo';
      toast.error('Erro ao ler arquivo', { description: detail });
    } finally {
      setReplaceParsing(false);
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    }
  };

  // Baixa o KMZ da geofence em edição (paridade com Rotas; o arquivo pode ser
  // re-importado depois via "Substituir por arquivo KMZ").
  const handleDownloadKmz = async (geofence) => {
    if (!geofence) return;
    try {
      const res = await geofenceApi.exportGeofence(geofence.id);
      const url = URL.createObjectURL(
        new Blob([res.data], { type: 'application/vnd.google-earth.kmz' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `geofence_${geofence.id}.kmz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading geofence KMZ:', error);
      toast.error('Erro ao baixar o KMZ da geofence');
    }
  };

  const handleCloseEditDialog = () => {
    setEditingGeofence(null);
    setEditGeometryType('polygon');
    setSelectedVertexIndex(null);
    setOriginalEditVertices([]);
    setEditVertices([]);
    setReplaceCandidates([]);
    setReplaceFileName('');
  };
  
  // Save edit
  const handleSaveEdit = async () => {
    if (!editingGeofence || !editName.trim()) return;
    if (editGeometryType === 'polygon' && editVertices.length < 3) {
      toast.error('Polígono inválido: mínimo de 3 vértices');
      return;
    }
    
    try {
      const payload = {
        name: editName.trim(),
        description: editDescription.trim() || null,
        color: normalizeHexColor(editColor),
        border_color: normalizeHexColor(editBorderColor, normalizeHexColor(editColor)),
        border_width: Math.max(1, Math.min(12, Number(editBorderWidth || 2))),
        fill_enabled: editFillMode !== 'outline',
        fill_opacity: getFillOpacityFromStyle(editFillMode, editTransparencyPercent),
        geofence_type: editType,
        visible: editVisible,
        route_id: editType === 'port' ? (editRouteId.trim().toUpperCase() || null) : null,
        // base_carga só é significativo p/ zona de carga (SPEC §5); backend limpa nos demais tipos.
        base_carga: editType === 'loading' ? editBaseCarga : false,
      };
      if (editGeometryType === 'polygon') {
        payload.polygon = toClosedGeoJsonPolygon(editVertices);
      }

      await geofenceApi.updateGeofence(editingGeofence.id, payload);
      
      setGeofences(prev => prev.map(gf => 
        gf.id === editingGeofence.id 
          ? { ...gf, ...payload }
          : gf
      ));
      
      toast.success('Geofence atualizada');
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating geofence:', error);
      toast.error('Erro ao atualizar geofence');
    }
  };
  
  // Delete geofence
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await geofenceApi.deleteGeofence(deleteConfirm.id);
      setGeofences(prev => prev.filter(gf => gf.id !== deleteConfirm.id));
      toast.success('Geofence excluída');
      setDeleteConfirm(null);
      
      if (selectedGeofenceId === deleteConfirm.id) {
        setSelectedGeofenceId(null);
      }
    } catch (error) {
      console.error('Error deleting geofence:', error);
      toast.error('Erro ao excluir geofence');
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupConfirm?.sourceGroupId) return;

    try {
      await geofenceApi.deleteGeofenceGroup(deleteGroupConfirm.sourceGroupId);
      setGeofences((prev) =>
        prev.filter((gf) => gf.source_group_id !== deleteGroupConfirm.sourceGroupId)
      );
      setManagedGroups((prev) => prev.filter((group) => group.id !== deleteGroupConfirm.sourceGroupId));
      toast.success(`Grupo "${deleteGroupConfirm.name}" excluído`);
      setDeleteGroupConfirm(null);
    } catch (error) {
      console.error('Error deleting geofence group:', error);
      toast.error('Erro ao excluir grupo de geofences');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const response = await geofenceApi.createGeofenceGroup({
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || null,
      });
      const group = response.data;
      setManagedGroups((prev) => [...prev, group]);
      setExpandedGroups((prev) => ({ ...prev, [group.id]: true }));
      setNewGroupName('');
      setNewGroupDescription('');
      setGroupDialogOpen(false);
      toast.success('Grupo criado');
    } catch (error) {
      console.error('Error creating geofence group:', error);
      toast.error('Erro ao criar grupo');
    }
  };

  const handleOpenMoveDialog = (geofence) => {
    setMoveDialogGeofence(geofence);
    setMoveTargetGroupId(geofence.source_group_id || '__none__');
  };

  const handleMoveGeofence = async () => {
    if (!moveDialogGeofence) return;
    const targetGroupId = moveTargetGroupId === '__none__' ? null : moveTargetGroupId;
    try {
      const response = await geofenceApi.moveGeofenceToGroup(moveDialogGeofence.id, targetGroupId);
      setGeofences((prev) => prev.map((gf) => (
        gf.id === moveDialogGeofence.id ? response.data : gf
      )));
      setMoveDialogGeofence(null);
      setMoveTargetGroupId('__none__');
      toast.success(targetGroupId ? 'Geofence transferida' : 'Geofence removida do grupo');
    } catch (error) {
      console.error('Error moving geofence:', error);
      toast.error('Erro ao transferir geofence');
    }
  };

  const handleOpenGroupStyleDialog = (group) => {
    const reference = group.items?.[0] || {};
    const baseColor = reference.color || '#FF6B35';
    setStyleDialogGroup(group);
    setGroupStyleColor(baseColor);
    setGroupStyleBorderColor(reference.border_color || baseColor);
    setGroupStyleBorderWidth(reference.border_width || 2);
    setGroupStyleFillMode(getFillMode(reference));
    setGroupStyleTransparencyPercent(getTransparencyPercent(reference, 0.3));
  };

  const handleSaveGroupStyle = async () => {
    if (!styleDialogGroup?.sourceGroupId) return;
    const payload = {
      color: normalizeHexColor(groupStyleColor),
      border_color: normalizeHexColor(groupStyleBorderColor, normalizeHexColor(groupStyleColor)),
      border_width: Math.max(1, Math.min(12, Number(groupStyleBorderWidth || 2))),
      fill_enabled: groupStyleFillMode !== 'outline',
      fill_opacity: getFillOpacityFromStyle(groupStyleFillMode, groupStyleTransparencyPercent),
    };

    try {
      await geofenceApi.updateGeofenceGroupStyle(styleDialogGroup.sourceGroupId, payload);
      setGeofences((prev) => prev.map((gf) => (
        gf.source_group_id === styleDialogGroup.sourceGroupId
          ? { ...gf, ...payload }
          : gf
      )));
      toast.success(`Estilo do grupo "${styleDialogGroup.name}" atualizado`);
      setStyleDialogGroup(null);
    } catch (error) {
      console.error('Error updating geofence group style:', error);
      toast.error('Erro ao atualizar estilo do grupo');
    }
  };

  const handleCreateCoordinate = async () => {
    if (!coordinateDialogGroup?.sourceGroupId || !coordinateName.trim()) return;
    const lat = Number(coordinateLat);
    const lon = Number(coordinateLon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      toast.error('Coordenada inválida');
      return;
    }
    try {
      const response = await geofenceApi.createManualGeofence({
        name: coordinateName.trim(),
        color: '#3B82F6',
        geometry_type: 'waypoint',
        point: { type: 'Point', coordinates: [lon, lat] },
        source_group_id: coordinateDialogGroup.sourceGroupId,
      });
      setGeofences((prev) => [...prev, response.data]);
      setCoordinateDialogGroup(null);
      setCoordinateName('');
      setCoordinateLat('');
      setCoordinateLon('');
      toast.success('Coordenada adicionada ao grupo');
    } catch (error) {
      console.error('Error creating coordinate geofence:', error);
      toast.error('Erro ao adicionar coordenada');
    }
  };

  const parsePolygonCoordinates = () => {
    const vertices = polygonCoordinatesText
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/[,\s;]+/).filter(Boolean).map(Number);
        if (parts.length < 2 || parts.some((value) => !Number.isFinite(value))) {
          return null;
        }
        const [lat, lon] = parts;
        return [lon, lat];
      });
    if (vertices.length < 3 || vertices.some((vertex) => !vertex)) {
      return null;
    }
    return toClosedGeoJsonPolygon(vertices);
  };

  const handleCreatePolygon = async () => {
    if (!polygonDialogGroup?.sourceGroupId || !polygonName.trim()) return;
    const polygon = parsePolygonCoordinates();
    if (!polygon) {
      toast.error('Informe ao menos 3 linhas no formato latitude, longitude');
      return;
    }
    try {
      const response = await geofenceApi.createManualGeofence({
        name: polygonName.trim(),
        color: '#FF6B35',
        geometry_type: 'polygon',
        polygon,
        source_group_id: polygonDialogGroup.sourceGroupId,
      });
      setGeofences((prev) => [...prev, response.data]);
      setPolygonDialogGroup(null);
      setPolygonName('');
      setPolygonCoordinatesText('');
      toast.success('Polígono adicionado ao grupo');
    } catch (error) {
      console.error('Error creating polygon geofence:', error);
      toast.error('Erro ao adicionar polígono');
    }
  };
  
  // Handle upload success
  const handleUploadSuccess = (newGeofence) => {
    loadGeofences();
    setSelectedGeofenceId(newGeofence.id);
  };

  const hasPolygonChanges = JSON.stringify(editVertices) !== JSON.stringify(originalEditVertices);
  
  return (
    <div className="page-container-fill gap-6" data-testid="geofences-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6 text-primary" />
            Geofences
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie cercas geográficas para monitoramento de equipamentos
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGroupDialogOpen(true)} data-testid="new-geofence-group-btn">
            <FolderPlus className="h-4 w-4 mr-2" />
            Novo Grupo
          </Button>
          <Button
            onClick={() => {
              setUploadTargetGroup(null);
              setUploadModalOpen(true);
            }}
            data-testid="new-geofence-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Geofence
          </Button>
        </div>
      </div>
      
      {/* Search and Refresh */}
      <div className="flex gap-4 shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar geofences..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="geofence-search"
          />
        </div>
        <Button variant="outline" onClick={loadGeofences}>
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>
      
      {/* Content */}
      <div className="grid grid-cols-1 grid-rows-2 lg:grid-cols-2 lg:grid-rows-1 gap-6 flex-1 min-h-0">
        {/* List */}
        <Card className="flex flex-col h-full min-h-[360px] min-w-0">
          <CardHeader className="shrink-0 pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Lista de Geofences ({filteredGeofences.length})</span>
              <Badge variant="outline">{groupedGeofences.length} grupos</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredGeofences.length === 0 ? (
              <div className="text-center py-8">
                <MapIcon className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {searchTerm ? 'Nenhuma geofence encontrada' : 'Nenhuma geofence cadastrada'}
                </p>
                {!searchTerm && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setUploadModalOpen(true)}
                  >
                    <FileUp className="h-4 w-4 mr-2" />
                    Upload de KMZ/KML
                  </Button>
                )}
              </div>
            ) : (
              <ScrollArea className="flex-1 min-h-0">
                <div className="space-y-2 pr-4">
                  {groupedGeofences.map((group) => (
                    <div
                      key={group.key}
                      className={cn(
                        "p-3 border rounded-lg transition-all",
                        group.partialEnabled
                          ? "border-primary/50"
                          : "hover:border-border/80"
                      )}
                      data-testid={`geofence-group-${group.key}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          className="flex items-start gap-2 text-left flex-1"
                          onClick={() => setExpandedGroups((prev) => ({
                            ...prev,
                            [group.key]: !prev[group.key],
                          }))}
                        >
                          {expandedGroups[group.key] ? (
                            <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground" />
                          )}
                          <Layers className="h-4 w-4 mt-0.5 text-primary" />
                          <div>
                            <p className="font-medium">{group.name}</p>
                            {group.sourceFilename && (
                              <p className="text-xs text-muted-foreground">
                                {group.sourceFilename}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {group.count} item(ns) • {group.enabledCount} ativo(s)
                            </p>
                          </div>
                        </button>

                        <div className="flex items-center gap-1">
                          {group.sourceGroupId && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUploadTargetGroup({ id: group.sourceGroupId, name: group.name });
                                  setUploadModalOpen(true);
                                }}
                                data-testid={`geofence-group-upload-${group.key}`}
                                title="Adicionar arquivos ao grupo"
                              >
                                <FileUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCoordinateDialogGroup(group);
                                }}
                                data-testid={`geofence-group-coordinate-${group.key}`}
                                title="Adicionar coordenada"
                              >
                                <LocateFixed className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPolygonDialogGroup(group);
                                }}
                                data-testid={`geofence-group-polygon-${group.key}`}
                                title="Adicionar polígono"
                              >
                                <Pentagon className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenGroupStyleDialog(group);
                                }}
                                disabled={group.count === 0}
                                data-testid={`geofence-group-style-${group.key}`}
                                title="Editar estilo do grupo"
                              >
                                <Palette className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          {group.sourceGroupId && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteGroupConfirm(group);
                              }}
                              data-testid={`geofence-group-delete-${group.key}`}
                              title="Excluir grupo completo"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                          {group.partialEnabled && (
                            <Badge variant="secondary" className="text-[10px]">Parcial</Badge>
                          )}
                          <Switch
                            checked={group.allEnabled}
                            onCheckedChange={() => handleToggleGroupEnabled(group)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={group.count === 0}
                            data-testid={`geofence-group-toggle-${group.key}`}
                          />
                        </div>
                      </div>

                      {expandedGroups[group.key] && (
                        <div className="mt-3 space-y-2">
                          {group.items
                            .slice()
                            .sort((a, b) => (a.source_item_index ?? 0) - (b.source_item_index ?? 0))
                            .map((gf) => (
                              <div
                                key={gf.id}
                                className={cn(
                                  "p-2 border rounded-md cursor-pointer transition-all bg-muted/10",
                                  selectedGeofenceId === gf.id
                                    ? "border-primary bg-primary/5"
                                    : "hover:border-border/80 hover:bg-muted/20"
                                )}
                                onClick={() => setSelectedGeofenceId(gf.id)}
                                data-testid={`geofence-item-${gf.id}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div
                                      className="w-3 h-3 rounded-full shrink-0"
                                      style={{ backgroundColor: gf.color }}
                                    />
                                  <div className="min-w-0">
                                      <p className="font-medium text-sm truncate">{getGeofenceDisplayName(gf)}</p>
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {getGeometryType(gf) === 'waypoint' ? 'Waypoint' : 'Polígono'}
                                        {gf.geofence_type && gf.geofence_type !== 'none' && (
                                          <> · {getGeofenceTypeLabel(gf.geofence_type)}</>
                                        )}
                                        {gf.visible === false && <> · oculto</>}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1 shrink-0">
                                    <Switch
                                      checked={gf.enabled}
                                      onCheckedChange={() => handleToggleEnabled(gf)}
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`geofence-toggle-${gf.id}`}
                                    />
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-muted-foreground">
                                    {(() => {
                                      const createdAt = getSafeDate(gf.created_at);
                                      if (!createdAt) return 'Data de criação indisponível';
                                      return `Criado ${formatDistanceToNow(createdAt, {
                                        addSuffix: true,
                                        locale: ptBR
                                      })} • ${formatDateTime(createdAt.toISOString(), { omitSeconds: true })}`;
                                    })()}
                                  </span>

                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => { e.stopPropagation(); handleEdit(gf); }}
                                      data-testid={`geofence-edit-${gf.id}`}
                                      title="Editar geofence"
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => { e.stopPropagation(); handleOpenMoveDialog(gf); }}
                                      data-testid={`geofence-move-${gf.id}`}
                                      title="Transferir entre grupos"
                                    >
                                      {gf.source_group_id ? <MoveRight className="h-3 w-3" /> : <Ungroup className="h-3 w-3" />}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(gf); }}
                                      data-testid={`geofence-delete-${gf.id}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
        
        {/* Map */}
        <Card className="flex flex-col h-full min-h-[360px] min-w-0">
          <CardHeader className="shrink-0 pb-2">
            <CardTitle className="text-sm">Mapa</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            <div className="h-full">
              <GeofenceMap
                geofences={filteredGeofences.filter(gf => gf.enabled && gf.visible !== false)}
                selectedGeofenceId={selectedGeofenceId}
                height="100%"
                onGeofenceClick={(gf) => setSelectedGeofenceId(gf.id)}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Upload Modal */}
      <GeofenceUploadModal
        open={uploadModalOpen}
        onOpenChange={(open) => {
          setUploadModalOpen(open);
          if (!open) setUploadTargetGroup(null);
        }}
        onSuccess={handleUploadSuccess}
        targetGroup={uploadTargetGroup}
      />

      {/* Create Group Modal */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo grupo de geofences</DialogTitle>
            <DialogDescription>
              Crie um grupo para organizar arquivos, polígonos e coordenadas relacionados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-group-name">Nome do grupo</Label>
              <Input
                id="new-group-name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Ex: Áreas de fundeio"
                data-testid="new-geofence-group-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-group-description">Descrição</Label>
              <Textarea
                id="new-group-description"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>Criar Grupo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Geofence Modal */}
      <Dialog open={!!moveDialogGeofence} onOpenChange={(open) => !open && setMoveDialogGeofence(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir geofence</DialogTitle>
            <DialogDescription>
              Escolha um grupo de destino para "{moveDialogGeofence?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Grupo de destino</Label>
            <Select value={moveTargetGroupId} onValueChange={setMoveTargetGroupId}>
              <SelectTrigger data-testid="geofence-move-target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sem grupo</SelectItem>
                {managedGroups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogGeofence(null)}>Cancelar</Button>
            <Button onClick={handleMoveGeofence}>Transferir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Coordinate Modal */}
      <Dialog open={!!coordinateDialogGroup} onOpenChange={(open) => !open && setCoordinateDialogGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar coordenada</DialogTitle>
            <DialogDescription>
              Crie um waypoint dentro do grupo "{coordinateDialogGroup?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={coordinateName} onChange={(e) => setCoordinateName(e.target.value)} placeholder="Ex: Boia norte" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input value={coordinateLat} onChange={(e) => setCoordinateLat(e.target.value)} placeholder="-23.9500" />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input value={coordinateLon} onChange={(e) => setCoordinateLon(e.target.value)} placeholder="-46.3300" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoordinateDialogGroup(null)}>Cancelar</Button>
            <Button onClick={handleCreateCoordinate} disabled={!coordinateName.trim()}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Polygon Modal */}
      <Dialog open={!!polygonDialogGroup} onOpenChange={(open) => !open && setPolygonDialogGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar polígono</DialogTitle>
            <DialogDescription>
              Informe uma coordenada por linha no grupo "{polygonDialogGroup?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={polygonName} onChange={(e) => setPolygonName(e.target.value)} placeholder="Ex: Área operacional A" />
            </div>
            <div className="space-y-2">
              <Label>Coordenadas</Label>
              <Textarea
                value={polygonCoordinatesText}
                onChange={(e) => setPolygonCoordinatesText(e.target.value)}
                rows={6}
                placeholder={"-23.9500, -46.3300\n-23.9510, -46.3200\n-23.9600, -46.3250"}
              />
              <p className="text-xs text-muted-foreground">Formato: latitude, longitude. O fechamento do polígono é automático.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolygonDialogGroup(null)}>Cancelar</Button>
            <Button onClick={handleCreatePolygon} disabled={!polygonName.trim()}>Adicionar Polígono</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Style Modal */}
      <Dialog open={!!styleDialogGroup} onOpenChange={(open) => !open && setStyleDialogGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar estilo do grupo</DialogTitle>
            <DialogDescription>
              Aplique cor, borda e preenchimento para todos os itens do grupo "{styleDialogGroup?.name}".
            </DialogDescription>
          </DialogHeader>
          <StyleControls
            color={groupStyleColor}
            setColor={setGroupStyleColor}
            borderColor={groupStyleBorderColor}
            setBorderColor={setGroupStyleBorderColor}
            borderWidth={groupStyleBorderWidth}
            setBorderWidth={setGroupStyleBorderWidth}
            fillMode={groupStyleFillMode}
            setFillMode={setGroupStyleFillMode}
            transparencyPercent={groupStyleTransparencyPercent}
            setTransparencyPercent={setGroupStyleTransparencyPercent}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setStyleDialogGroup(null)}>Cancelar</Button>
            <Button onClick={handleSaveGroupStyle}>Aplicar ao Grupo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Modal */}
      <Dialog open={!!editingGeofence} onOpenChange={(open) => !open && handleCloseEditDialog()}>
        <DialogContent className="flex max-h-[90vh] flex-col">
          <DialogHeader>
            <DialogTitle>Editar Geofence</DialogTitle>
            <DialogDescription>
              Altere as propriedades da cerca geográfica
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 min-h-0 overflow-y-auto pr-1">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nome</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Nome da geofence"
                data-testid="edit-geofence-name"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrição</label>
              <Input
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Descrição opcional"
                data-testid="edit-geofence-description"
              />
            </div>

            {/* Classificação operacional (tipo / visibilidade / rota do porto) */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Tipo / Classificação</Label>
                <Select value={editType} onValueChange={setEditType}>
                  <SelectTrigger data-testid="edit-geofence-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GEOFENCE_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Classifica a área (segura, restrita/perigosa, carregamento, porto de descarregamento, área ANM).
                </p>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-sm font-medium">Visível no mapa</Label>
                  <p className="text-xs text-muted-foreground">
                    Oculta a geofence do mapa sem desativar os alertas vinculados.
                  </p>
                </div>
                <Switch
                  checked={editVisible}
                  onCheckedChange={setEditVisible}
                  data-testid="edit-geofence-visible"
                />
              </div>

              {editType === 'port' && (
                <div className="space-y-2 border-t pt-3">
                  <Label className="text-sm font-medium">Rota vinculada</Label>
                  <Select
                    value={editRouteId || '__none__'}
                    onValueChange={(value) => setEditRouteId(value === '__none__' ? '' : value)}
                  >
                    <SelectTrigger data-testid="edit-geofence-route">
                      <SelectValue placeholder="— selecione a rota —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— sem rota —</SelectItem>
                      {editRouteId && !routes.some((route) => route.id === editRouteId) && (
                        <SelectItem value={editRouteId}>{editRouteId} (não cadastrada)</SelectItem>
                      )}
                      {routes.map((route) => (
                        <SelectItem key={route.id} value={route.id}>
                          {`Rota ${route.id}${route.destino ? ` — ${route.destino}` : ''}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Rota que chega a este porto. Cadastre rotas no menu "Rotas".
                  </p>
                </div>
              )}

              {editType === 'loading' && (
                <div className="flex items-center justify-between gap-3 border-t pt-3">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium">Base de carga (fecha a viagem no retorno)</Label>
                    <p className="text-xs text-muted-foreground">
                      Marque quando esta é a base FIXA da operação (ex.: Guaíra). A viagem só fecha
                      ao retornar aqui — o trajeto de volta vazio entra na viagem certa. Deixe
                      desmarcado quando a viagem deve fechar no fim da descarga (padrão).
                    </p>
                  </div>
                  <Switch
                    checked={editBaseCarga}
                    onCheckedChange={setEditBaseCarga}
                    data-testid="edit-geofence-base-carga"
                  />
                </div>
              )}
            </div>

            <StyleControls
              color={editColor}
              setColor={setEditColor}
              borderColor={editBorderColor}
              setBorderColor={setEditBorderColor}
              borderWidth={editBorderWidth}
              setBorderWidth={setEditBorderWidth}
              fillMode={editFillMode}
              setFillMode={setEditFillMode}
              transparencyPercent={editTransparencyPercent}
              setTransparencyPercent={setEditTransparencyPercent}
            />

            {editGeometryType === 'polygon' ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="text-sm font-medium">Área (arraste os vértices)</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadKmz(editingGeofence)}
                      data-testid="edit-geofence-download-kmz"
                      title="Baixar o KMZ atual desta geofence"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Baixar KMZ
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => editFileInputRef.current?.click()}
                      disabled={replaceParsing}
                      data-testid="edit-geofence-replace-file"
                      title="Substituir vértices a partir de um arquivo KMZ/KML"
                    >
                      {replaceParsing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileUp className="h-4 w-4 mr-2" />
                      )}
                      Substituir por arquivo KMZ
                    </Button>
                  </div>
                  <input
                    ref={editFileInputRef}
                    type="file"
                    accept=".kmz,.kml"
                    className="hidden"
                    onChange={(e) => handleSelectReplaceFile(e.target.files)}
                  />
                </div>
                <div className="h-[320px] rounded-lg border overflow-hidden">
                  {editVertices.length >= 3 ? (
                    <MapContainer
                      center={[editVertices[0][1], editVertices[0][0]]}
                      zoom={15}
                      className="h-full w-full"
                    >
                      <MapLayerControl defaultLayer="carto_dark" />
                      <FitEditBounds vertices={editVertices} fitToken={editFitToken} />

                      <Polygon
                        positions={editVertices.map(([lon, lat]) => [lat, lon])}
                        pathOptions={{
                          color: normalizeHexColor(editBorderColor, normalizeHexColor(editColor)),
                          fillColor: editColor,
                          fillOpacity: getFillOpacityFromStyle(editFillMode, editTransparencyPercent),
                          weight: Math.max(1, Math.min(12, Number(editBorderWidth || 2)))
                        }}
                      />

                      {editVertices.map(([lon, lat], index) => (
                        <Marker
                          key={`vertex-${index}-${lon}-${lat}`}
                          position={[lat, lon]}
                          icon={createVertexIcon(index === selectedVertexIndex)}
                          draggable
                          eventHandlers={{
                            click: () => setSelectedVertexIndex(index),
                            dragstart: () => setSelectedVertexIndex(index),
                            dragend: (event) => {
                              const pos = event.target.getLatLng();
                              handleVertexDrag(index, pos.lat, pos.lng);
                            },
                            contextmenu: (event) => {
                              event.originalEvent?.preventDefault();
                              event.originalEvent?.stopPropagation();
                              handleRemoveVertex(index);
                            },
                            dblclick: (event) => {
                              event.originalEvent?.preventDefault();
                              event.originalEvent?.stopPropagation();
                              handleRemoveVertex(index);
                            }
                          }}
                        />
                      ))}

                      {editVertices.map(([lon1, lat1], index) => {
                        const nextIndex = (index + 1) % editVertices.length;
                        const [lon2, lat2] = editVertices[nextIndex];
                        return (
                          <Polyline
                            key={`edge-${index}`}
                            positions={[[lat1, lon1], [lat2, lon2]]}
                            pathOptions={{ color: normalizeHexColor(editBorderColor, normalizeHexColor(editColor)), weight: 12, opacity: 0 }}
                            eventHandlers={{
                              dblclick: (event) => {
                                event.originalEvent?.preventDefault();
                                event.originalEvent?.stopPropagation();
                                handleInsertVertex(index);
                              }
                            }}
                          />
                        );
                      })}
                    </MapContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                      Polígono indisponível para edição
                    </div>
                  )}
                </div>

                <div className="text-xs text-muted-foreground space-y-1">
                  <p>1. Arraste um vértice para reposicionar sem perder o zoom atual.</p>
                  <p>2. Duplo clique na aresta para adicionar novo vértice.</p>
                  <p>3. Duplo clique ou clique direito no vértice para remover.</p>
                  <p>4. Use “Resetar Área” para restaurar o polígono original antes de salvar.</p>
                  <p>5. “Substituir por arquivo KMZ” substitui todos os vértices a partir de um arquivo, mantendo os alertas vinculados a esta geofence.</p>
                  <p>6. “Baixar KMZ” exporta a geometria atual — o arquivo pode ser re-importado depois.</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={handleResetPolygon}
                    disabled={!hasPolygonChanges}
                  >
                    Resetar Área
                  </Button>
                  {selectedVertexIndex !== null && (
                    <Badge variant="outline">
                      Vértice selecionado: V{selectedVertexIndex + 1}
                    </Badge>
                  )}
                </div>

                {editVertices.length > 0 && (
                  <div className="flex flex-wrap gap-2 max-h-[80px] overflow-auto">
                    {editVertices.map((_, index) => (
                      <Button
                        key={`remove-vertex-${index}`}
                        type="button"
                        size="sm"
                        variant={selectedVertexIndex === index ? "default" : "outline"}
                        onClick={() => handleRemoveVertex(index)}
                      >
                        Remover V{index + 1}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de Geometria</label>
                <div className="rounded-lg border bg-muted/20 p-3 text-sm">
                  Waypoint (ponto único). Edição de coordenadas não disponível nesta tela.
                </div>
                <div className="text-xs text-muted-foreground">
                  Coordenadas atuais: {editingGeofence?.point?.coordinates?.[1] ?? '--'}, {editingGeofence?.point?.coordinates?.[0] ?? '--'}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={!editName.trim()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Choose polygon from file (when the KMZ/KML has more than one) */}
      <Dialog open={replaceCandidates.length > 0} onOpenChange={(open) => !open && setReplaceCandidates([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escolher polígono do arquivo</DialogTitle>
            <DialogDescription>
              O arquivo "{replaceFileName}" contém {replaceCandidates.length} polígonos. Escolha qual substituirá os vértices desta geofence.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[320px]">
            <div className="space-y-2 pr-3">
              {replaceCandidates.map((geometry, index) => {
                const vertexCount = getEditableVerticesFromPolygon(geometry?.polygon).length;
                const label = ensureString(geometry?.name, '').trim() || `Polígono #${index + 1}`;
                return (
                  <button
                    key={`replace-candidate-${index}`}
                    type="button"
                    className="w-full text-left p-3 border rounded-md transition-all hover:border-primary hover:bg-primary/5"
                    onClick={() => {
                      applyImportedPolygon(geometry);
                      setReplaceCandidates([]);
                    }}
                    data-testid={`replace-candidate-${index}`}
                  >
                    <p className="font-medium text-sm truncate">{label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{vertexCount} vértices</p>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceCandidates([])}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir geofence?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso excluirá a geofence "{deleteConfirm?.name}" e todas as regras de alerta vinculadas.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Group Confirmation */}
      <AlertDialog open={!!deleteGroupConfirm} onOpenChange={(open) => !open && setDeleteGroupConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo completo?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso excluirá o grupo "{deleteGroupConfirm?.name}" com {deleteGroupConfirm?.count || 0} item(ns),
              incluindo regras/eventos/histórico associados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir Grupo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GeofencesPage;
